import {
  type HttpEvent,
  type HttpHandler,
  type HttpInterceptor,
  type HttpRequest
} from '@angular/common/http'
import { Injectable, InjectionToken, inject } from '@angular/core'
// Hub libraries
import { HubConfigurationService } from '@inside-hub-app/hub-configuration'
// 3rd party
import { type Observable } from 'rxjs'
// Feature
import { HubAuthStore } from '../services'
import { AuthHeaders } from '../enums'

type ExcludedUrlRegex = {
  pattern: string
  methods?: Array<HttpRequest<unknown>['method']>
}

type ServicesEnvironment = {
  pp: {
    environment: {
      domain: string
      baseUrl: string
      authorizationExcludedUrls: ExcludedUrlRegex[]
    }
  }
}
// This token is used to provide the excluded URLs for the authentication interceptor from modules othen than ppModule
// Mostly for other apps that has different app-config url and provides configuration over ppModule.forRoot()
// This token is imported in ppModule and value is set in ppModule.forRoot() method
export const AUTH_EXCLUDED_URLS_TOKEN = new InjectionToken<ExcludedUrlRegex[]>('AUTH_EXCLUDED_URLS_TOKEN')

@Injectable()
export class XAuthTokenInterceptor implements HttpInterceptor {
  private readonly hubAuthStore = inject(HubAuthStore)
  private readonly hubConfigurationService = inject(HubConfigurationService)
  private readonly excludedUrlsToken = inject(AUTH_EXCLUDED_URLS_TOKEN, { optional: true })

  intercept (request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.hubAuthStore.token()

    const env = this.hubConfigurationService.get('services') as unknown as ServicesEnvironment
    const excludedUrls = this.excludedUrlsToken ?? env?.pp?.environment?.authorizationExcludedUrls ?? []

    const isExcludedUrl = excludedUrls.some(rule => this.isUrlExcluded(request, rule))

    if (isExcludedUrl || !token || request.headers.has(AuthHeaders.XAuthToken)) {
      return next.handle(request)
    }

    const clonedRequest = request.clone({
      setHeaders: { [AuthHeaders.XAuthToken]: token }
    })

    return next.handle(clonedRequest)
  }

  private isUrlExcluded (request: HttpRequest<unknown>, rule: ExcludedUrlRegex): boolean {
    const method = request.method.toUpperCase()
    const methodMatches = !rule.methods?.length || rule.methods.includes(method)
    const urlMatches = new RegExp(rule.pattern).test(request.url)
    return methodMatches && urlMatches
  }
}
